import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'

interface DateRangeFilterProps {
  startDate: string
  endDate: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  onApplyFilter: () => void
  onClearFilter: () => void
  onQuickFilter: (startDate: string, endDate: string) => void
  comparePeriods: number
  onComparePeriodsChange: (periods: number) => void
  selectedPeriod: string
  onPeriodChange: (period: string) => void
  showComparePeriods: boolean
}

export default function DateRangeFilter({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApplyFilter,
  onClearFilter,
  onQuickFilter,
  comparePeriods,
  onComparePeriodsChange,
  selectedPeriod,
  onPeriodChange,
  showComparePeriods,
}: DateRangeFilterProps) {
  const getQuickFilterDates = (period: string) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    // Format dates in local timezone to avoid UTC conversion issues
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    switch (period) {
      case '7days': {
        const sevenDaysAgo = new Date(today)
        sevenDaysAgo.setDate(today.getDate() - 6) // Changed from -7 to -6 to get exactly 7 days
        return {
          start: formatLocalDate(sevenDaysAgo),
          end: formatLocalDate(today),
        }
      }

      case '14days': {
        const fourteenDaysAgo = new Date(today)
        fourteenDaysAgo.setDate(today.getDate() - 13) // Changed from -14 to -13 to get exactly 14 days
        return {
          start: formatLocalDate(fourteenDaysAgo),
          end: formatLocalDate(today),
        }
      }

      case '30days': {
        const thirtyDaysAgo = new Date(today)
        thirtyDaysAgo.setDate(today.getDate() - 29) // Changed from -30 to -29 to get exactly 30 days
        return {
          start: formatLocalDate(thirtyDaysAgo),
          end: formatLocalDate(today),
        }
      }
      case '90days': {
        const ninetyDaysAgo = new Date(today)
        ninetyDaysAgo.setDate(today.getDate() - 89) // Changed from -90 to -89 to get exactly 90 days
        return {
          start: formatLocalDate(ninetyDaysAgo),
          end: formatLocalDate(today),
        }
      }
      case 'thisweek': {
        // Current week (Monday to Sunday)
        const dayOfWeek = today.getDay() // 0=Sunday, 1=Monday, ..., 6=Saturday
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // Sunday should go back 6 days
        const monday = new Date(today)
        monday.setDate(today.getDate() - daysFromMonday)
        const sunday = new Date(monday)
        sunday.setDate(monday.getDate() + 6)
        return { start: formatLocalDate(monday), end: formatLocalDate(sunday) }
      }
      case 'lastweek': {
        // Last week (Monday to Sunday)
        const lastWeekToday = new Date(today)
        lastWeekToday.setDate(today.getDate() - 7)
        const lastWeekDayOfWeek = lastWeekToday.getDay()
        const lastWeekDaysFromMonday =
          lastWeekDayOfWeek === 0 ? 6 : lastWeekDayOfWeek - 1
        const lastMonday = new Date(lastWeekToday)
        lastMonday.setDate(lastWeekToday.getDate() - lastWeekDaysFromMonday)
        const lastSunday = new Date(lastMonday)
        lastSunday.setDate(lastMonday.getDate() + 6)
        return {
          start: formatLocalDate(lastMonday),
          end: formatLocalDate(lastSunday),
        }
      }
      case 'thismonth': {
        // Current month (1st to last day)
        const firstDayThisMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        )
        const lastDayThisMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0
        )
        return {
          start: formatLocalDate(firstDayThisMonth),
          end: formatLocalDate(lastDayThisMonth),
        }
      }
      case 'lastmonth': {
        // Last month (1st to last day)
        const firstDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        )
        const lastDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        )
        return {
          start: formatLocalDate(firstDayLastMonth),
          end: formatLocalDate(lastDayLastMonth),
        }
      }
      case 'thisquarter': {
        // Current quarter
        const currentQuarter = Math.floor(today.getMonth() / 3)
        const quarterStartMonth = currentQuarter * 3
        const quarterStart = new Date(today.getFullYear(), quarterStartMonth, 1)
        const quarterEnd = new Date(
          today.getFullYear(),
          quarterStartMonth + 3,
          0
        )
        return {
          start: formatLocalDate(quarterStart),
          end: formatLocalDate(quarterEnd),
        }
      }
      case 'lastquarter': {
        // Last quarter
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1
        let quarterYear = today.getFullYear()
        let lastQuarterStartMonth = lastQuarter * 3

        if (lastQuarter < 0) {
          // If we're in Q1, last quarter is Q4 of previous year
          quarterYear = today.getFullYear() - 1
          lastQuarterStartMonth = 9 // October (Q4 starts)
        }

        const lastQuarterStart = new Date(quarterYear, lastQuarterStartMonth, 1)
        const lastQuarterEnd = new Date(
          quarterYear,
          lastQuarterStartMonth + 3,
          0
        )
        return {
          start: formatLocalDate(lastQuarterStart),
          end: formatLocalDate(lastQuarterEnd),
        }
      }
      default:
        return { start: '', end: '' }
    }
  }

  const handleQuickFilter = (period: string) => {
    const { start, end } = getQuickFilterDates(period)
    onPeriodChange(period)
    onQuickFilter(start, end)
  }

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Filter by Date Range
      </Typography>

      {/* Quick Filter Buttons */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Quick Filters:
        </Typography>

        {/* Day-based filters */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.5, fontWeight: 'medium' }}
        >
          Rolling periods:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant={selectedPeriod === '7days' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleQuickFilter('7days')}
          >
            Last 7 Days
          </Button>
          <Button
            variant={selectedPeriod === '14days' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleQuickFilter('14days')}
          >
            Last 14 Days
          </Button>
          <Button
            variant={selectedPeriod === '30days' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleQuickFilter('30days')}
          >
            Last 30 Days
          </Button>
          <Button
            variant={selectedPeriod === '90days' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleQuickFilter('90days')}
          >
            Last 90 Days
          </Button>
        </Box>

        {/* Calendar-based filters */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.5, fontWeight: 'medium' }}
        >
          Calendar periods:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant={selectedPeriod === 'thisweek' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleQuickFilter('thisweek')}
          >
            This Week
          </Button>
          <Button
            variant={selectedPeriod === 'lastweek' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleQuickFilter('lastweek')}
          >
            Last Week
          </Button>
          <Button
            variant={selectedPeriod === 'thismonth' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleQuickFilter('thismonth')}
          >
            This Month
          </Button>
          <Button
            variant={selectedPeriod === 'lastmonth' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleQuickFilter('lastmonth')}
          >
            Last Month
          </Button>
          <Button
            variant={
              selectedPeriod === 'thisquarter' ? 'contained' : 'outlined'
            }
            size="small"
            onClick={() => handleQuickFilter('thisquarter')}
          >
            This Quarter
          </Button>
          <Button
            variant={
              selectedPeriod === 'lastquarter' ? 'contained' : 'outlined'
            }
            size="small"
            onClick={() => handleQuickFilter('lastquarter')}
          >
            Last Quarter
          </Button>
        </Box>
      </Box>

      {/* Custom Date Range */}
      <Box
        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => onStartDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => onEndDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        {showComparePeriods && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Compare with periods</InputLabel>
            <Select
              value={comparePeriods}
              label="Compare with periods"
              onChange={(e) => onComparePeriodsChange(Number(e.target.value))}
            >
              <MenuItem value={0}>No comparison</MenuItem>
              <MenuItem value={1}>Compare with 1 period</MenuItem>
              <MenuItem value={2}>Compare with 2 periods</MenuItem>
              <MenuItem value={3}>Compare with 3 periods</MenuItem>
              <MenuItem value={4}>Compare with 4 periods</MenuItem>
              <MenuItem value={5}>Compare with 5 periods</MenuItem>
            </Select>
          </FormControl>
        )}
        <Button
          variant="contained"
          onClick={onApplyFilter}
          disabled={!startDate && !endDate}
          size="small"
        >
          Apply Filter
        </Button>
        <Button variant="outlined" onClick={onClearFilter} size="small">
          Clear Filter
        </Button>
      </Box>
      {(startDate || endDate) && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {startDate && endDate
              ? `Showing data from ${startDate} to ${endDate}`
              : startDate
              ? `Showing data from ${startDate} onwards`
              : `Showing data up to ${endDate}`}
          </Typography>

          {showComparePeriods && comparePeriods > 0 && startDate && endDate && (
            <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
              {`Comparing with ${comparePeriods} previous period${
                comparePeriods > 1 ? 's' : ''
              }`}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  )
}
